import React, { useState } from 'react';
import Header from '../components/Header';
import { useNavigate } from 'react-router-dom';
import { SW_VERSION } from '../config';
import '../styles.css';

function Help() {
  const [syncStatus, setSyncStatus] = useState('offline');
  const [expandedSections, setExpandedSections] = useState({});
  const navigate = useNavigate();

  React.useEffect(() => {
    setSyncStatus(navigator.onLine ? 'synced' : 'offline');
  }, []);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const ExpandableSection = ({ id, title, children }) => (
    <div className="settings-card">
      <div 
        className="expandable-header" 
        onClick={() => toggleSection(id)}
        style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <h1 style={{ margin: 0 }}>{title}</h1>
        <span className="material-icons">
          {expandedSections[id] ? 'expand_less' : 'expand_more'}
        </span>
      </div>
      {expandedSections[id] && (
        <div className="expandable-content" style={{ marginTop: '20px' }}>
          {children}
        </div>
      )}
    </div>
  );

  return (
    <div>
      <Header syncStatus={syncStatus} />
      <div className="settings-container">
        <div className="page-title">
          <h1>Help & Support</h1>
          <button onClick={() => navigate('/')} className="close-button">✕</button>
        </div>
        
        <div className="settings-card">
          <h1>Submitting a Bug Report</h1>
          <p>If you encounter any issues or bugs, please report them to our support team.</p>
          <p>Email: support@hotsetup.com</p>
          <p>Please include:</p>
          <ul>
            <li>A detailed description of the issue</li>
            <li>Steps to reproduce the problem</li>
            <li>Your browser and device information</li>
            <li>Screenshots if applicable</li>
          </ul>
          <p style={{ marginTop: '20px', fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
            Hot Setup Version: {SW_VERSION}
          </p>
        </div>
        
        <div className="settings-card">
          <h1>Quick Start Guide</h1>
          <p>Welcome to Hot Setup! Follow these steps to get started with tracking your vehicle setups.</p>
          <h3>Step 1: Create Your First Vehicle</h3>
          <p>Everything starts with a vehicle. Click "Add Vehicle" on the main dashboard.</p>
          <ul>
            <li>Choose imperial or metric units for your parameters</li>
            <li>Add a vehicle name and optional image</li>
            <li>The vehicle contains all your baseline setup parameters</li>
            <li>Parameters are organized in tabs: Suspension, Tires, Brakes, Aero, Drivetrain, Engine, Chassis, Electronics</li>
            <li>You can add custom parameters or groups within each tab</li>
            <li>Save your vehicle - this becomes your master setup template</li>
          </ul>
          <h3>Step 2: Create an Event</h3>
          <p>Events group related sessions together. Click "Add Event" on the main dashboard.</p>
          <ul>
            <li>You must have at least one vehicle before creating an event</li>
            <li>Select which vehicle this event will use</li>
            <li>Choose the circuit/track for the event</li>
            <li>Set event dates and add any notes</li>
            <li>Events appear as cards on your main dashboard</li>
          </ul>
          <h3>Step 3: Create Sessions Within Events</h3>
          <p>Sessions are where you record your actual setup changes and performance data.</p>
          <ul>
            <li>Click on an event card to open it</li>
            <li>Click "New Session" to create a session</li>
            <li>Each session starts with the vehicle's baseline parameters</li>
            <li>You can modify parameters for this specific session</li>
            <li>Sessions can be duplicated from previous ones to save time</li>
          </ul>
          <h3>Step 4: Use Setup Sheets</h3>
          <p>Each session has a setup sheet where you adjust parameters.</p>
          <ul>
            <li>Click the assignment icon (setup sheet) next to any session</li>
            <li>Modify parameters from the baseline vehicle setup</li>
            <li>Changes are saved per session, not affecting the master vehicle</li>
            <li>You can duplicate setups from other sessions or events</li>
            <li>Add notes for each parameter change</li>
          </ul>
          <h3>Step 5: Record Session Data</h3>
          <p>Track your performance and conditions for each session.</p>
          <ul>
            <li>Click on a session card to open session data</li>
            <li>Record weather conditions (temperature, humidity, wind)</li>
            <li>Manually enter lap times - the app finds your best lap</li>
            <li>Add general balance notes about vehicle handling</li>
            <li>All data is saved automatically and synced</li>
          </ul>
          <h3>Step 6: Use Track Maps</h3>
          <p>Add detailed corner-by-corner notes and balance information.</p>
          <ul>
            <li>Click "Track Map" in session data</li>
            <li>Click anywhere on the track to add a note</li>
            <li>Describe issues or setup changes needed for that corner</li>
            <li>Use balance sliders to rate handling in 5 phases: TB, EN, MD, TP, EX</li>
            <li>Values from -10 (understeer) to +10 (oversteer)</li>
            <li>Visualize your vehicle's balance across the entire track</li>
          </ul>
        </div>

        <ExpandableSection id="navigation" title="Navigating the App">
          <p>Hot Setup is designed to help motorsports participants track and optimize their vehicle setups across different events and sessions.</p>
          <p>The main dashboard displays your recent events and vehicles. Events are associated with specific vehicles and contain multiple sessions.</p>
          <p>Use the hamburger menu in the header to access Settings, Account, and Help. The sync icon shows your connection status.</p>
          <p>The app works offline - your data is cached locally and synced when online.</p>
          <h3>Key Navigation Elements</h3>
          <ul>
            <li><strong>Main Dashboard:</strong> View recent events and vehicles, create new ones</li>
            <li><strong>Event Pages:</strong> Manage sessions within an event</li>
            <li><strong>Vehicle Editor:</strong> Configure vehicle parameters and settings</li>
            <li><strong>Session Setup:</strong> Adjust parameters for specific sessions</li>
            <li><strong>Session Data:</strong> Record performance data and conditions</li>
            <li><strong>Settings:</strong> Manage circuits, backups, and app preferences</li>
          </ul>
          <h3>Data Hierarchy</h3>
          <p>Understanding the app's data structure helps you organize your information effectively:</p>
          <ul>
            <li><strong>Vehicles:</strong> Master templates containing all setup parameters</li>
            <li><strong>Events:</strong> Group sessions by date, location, and vehicle</li>
            <li><strong>Sessions:</strong> Individual track sessions with specific setups and data</li>
            <li><strong>Circuits:</strong> Track information used across multiple events</li>
          </ul>
        </ExpandableSection>

        <ExpandableSection id="vehicles" title="Creating and Managing Vehicles">
          <p>Vehicles are the foundation of your setup data. Each vehicle contains baseline parameters that sessions can inherit and modify.</p>
          <h3>Creating a New Vehicle</h3>
          <ul>
            <li>From the main dashboard, click "Add Vehicle"</li>
            <li>Choose between imperial (inches, psi, °F) or metric (mm, bar, °C) units</li>
            <li>Optionally duplicate an existing vehicle to use as a starting point</li>
            <li>The app creates default parameter groups for each system</li>
          </ul>
          <h3>Vehicle Editor Overview</h3>
          <p>The Vehicle Editor organizes parameters into tabs for different vehicle systems:</p>
          <ul>
            <li><strong>Info:</strong> Vehicle name, image, and general notes</li>
            <li><strong>Suspension:</strong> Springs, dampers, anti-roll bars, alignment, weights</li>
            <li><strong>Tires:</strong> Pressure, compound, wear tracking</li>
            <li><strong>Brakes:</strong> Pad compound, rotor size, bias</li>
            <li><strong>Aero:</strong> Wing angles, downforce settings</li>
            <li><strong>Drivetrain:</strong> Gear ratios, differential settings, traction control</li>
            <li><strong>Engine:</strong> Power curve data, fuel mixture, ignition timing</li>
            <li><strong>Electronics:</strong> ECU maps, sensors, data logging</li>
          </ul>
          <h3>Editing Vehicle Parameters</h3>
          <ul>
            <li>Click on a vehicle card to open the Vehicle Editor</li>
            <li>Use tabs to navigate between different systems</li>
            <li>Each parameter has min/max values and step increments</li>
            <li>Add custom parameters by clicking the "+" button</li>
            <li>Create custom groups to organize related parameters</li>
            <li>Drag and drop parameters between groups using the handle (☰)</li>
            <li>Move groups up/down with arrow buttons</li>
            <li>Reorder tabs by dragging them</li>
          </ul>
          <h3>Vehicle Images and Notes</h3>
          <ul>
            <li>Upload vehicle photos (automatically compressed to 10MB max)</li>
            <li>Add general notes about the vehicle</li>
            <li>Images are stored in Firebase Storage and cached locally</li>
            <li>Old images are automatically deleted when replaced</li>
          </ul>
          <h3>Duplicating Vehicles</h3>
          <ul>
            <li>Create variations of existing vehicles</li>
            <li>Preserves all parameters, groups, and custom settings</li>
            <li>Useful for testing different configurations</li>
            <li>Only the name and image are reset for the duplicate</li>
          </ul>
          <h3>Deleting Vehicles</h3>
          <ul>
            <li>Click "Delete Vehicle" in the editor</li>
            <li>This also deletes all associated sessions and events</li>
            <li>A confirmation dialog prevents accidental deletion</li>
            <li>Use with caution - consider backing up first</li>
          </ul>
        </ExpandableSection>

        <ExpandableSection id="events" title="Working with Events">
          <p>Events organize your sessions by date, location, and vehicle. They provide context for your setup data.</p>
          <h3>Creating Events</h3>
          <ul>
            <li>You need at least one vehicle before creating events</li>
            <li>Click "Add Event" on the main dashboard</li>
            <li>Select which vehicle this event will use</li>
            <li>Choose the circuit/track for the event</li>
            <li>Set start and end dates (optional for single-day events)</li>
            <li>Upload event images and add descriptions</li>
          </ul>
          <h3>Event Editor Features</h3>
          <ul>
            <li>Associate events with specific vehicles and circuits</li>
            <li>Set date ranges for multi-day events</li>
            <li>Upload promotional or track images</li>
            <li>Add notes about the event (weather expectations, goals, etc.)</li>
            <li>Edit event details after creation</li>
          </ul>
          <h3>Event Display and Navigation</h3>
          <ul>
            <li>Events show on the main dashboard with background images</li>
            <li>Date ranges are formatted automatically (e.g., "Jan 15-16, 2024")</li>
            <li>Click any event card to view its sessions</li>
            <li>Events are sorted by start date (most recent first)</li>
          </ul>
          <h3>Managing Multiple Sessions</h3>
          <ul>
            <li>Each event can contain multiple sessions</li>
            <li>Sessions inherit the event's vehicle and circuit</li>
            <li>Track setup evolution across an event</li>
            <li>Compare performance between sessions</li>
          </ul>
          <h3>Editing Events</h3>
          <ul>
            <li>From the event page, click "Edit Event"</li>
            <li>Change dates, circuits, or vehicle associations</li>
            <li>Changes affect all sessions within the event</li>
            <li>Be careful when changing vehicles - may affect session compatibility</li>
          </ul>
        </ExpandableSection>

        <ExpandableSection id="sessions" title="Managing Sessions">
          <p>Sessions are where setup changes and performance data come together. Each session represents a specific track outing.</p>
          <h3>Creating Sessions</h3>
          <ul>
            <li>Open an event and click "New Session"</li>
            <li>Sessions inherit the event's vehicle and circuit</li>
            <li>Each session gets a timestamp and can have a custom name</li>
            <li>Sessions appear immediately in the event's session list</li>
          </ul>
          <h3>Session Setup Sheets</h3>
          <p>Each session has its own setup sheet where you can adjust parameters from the vehicle's baseline:</p>
          <ul>
            <li>Click the assignment icon (setup sheet) next to any session</li>
            <li>Starts with vehicle baseline parameters</li>
            <li>Modify any parameter for this specific session</li>
            <li>Changes are session-specific, not affecting the master vehicle</li>
            <li>Add notes explaining why you made each change</li>
            <li>Duplicate setups from other sessions to speed up data entry</li>
            <li>Access power curves and gearing calculator</li>
          </ul>
          <h3>Duplicating Setups</h3>
          <ul>
            <li>Copy parameter changes from previous sessions</li>
            <li>Useful when making incremental setup adjustments</li>
            <li>Preserves all notes and custom parameters</li>
            <li>Can duplicate across different events and vehicles</li>
          </ul>
          <h3>Session Data Recording</h3>
          <ul>
            <li>Click on session cards to open the data view</li>
            <li>Record weather conditions (temperature, humidity, wind)</li>
            <li>Enter lap times manually (MM:SS.sss format)</li>
            <li>App automatically calculates best lap and lap count</li>
            <li>Add balance notes describing overall handling</li>
            <li>All data saves automatically and syncs across devices</li>
          </ul>
          <h3>Session Display</h3>
          <ul>
            <li>Sessions show in both card and table format within events</li>
            <li>Display best lap time, total laps, and notes</li>
            <li>Setup sheet icon allows quick access to parameter changes</li>
            <li>Sessions are sorted by time (most recent first)</li>
          </ul>
          <h3>Lap Timing</h3>
          <ul>
            <li>Record lap times manually in Session Data</li>
            <li>Enter times in MM:SS.sss format (e.g., 1:23.456)</li>
            <li>The app automatically identifies the best lap</li>
            <li>Track lap count and consistency</li>
            <li>Use lap times to correlate with setup changes and weather</li>
          </ul>
          <h3>Weather Tracking</h3>
          <ul>
            <li>Record ambient temperature, track temperature, humidity</li>
            <li>Wind speed and direction</li>
            <li>Temperatures auto-convert between Celsius/Fahrenheit</li>
            <li>Wind speed converts between km/h and mph</li>
            <li>Weather data helps explain performance variations</li>
          </ul>
        </ExpandableSection>

        <ExpandableSection id="trackmaps" title="Using Track Maps for Detailed Analysis">
          <p>Track maps allow precise, location-specific setup notes and balance analysis.</p>
          <h3>Opening Track Maps</h3>
          <ul>
            <li>From session data, click the "Track Map" button</li>
            <li>Loads the circuit's track map image</li>
            <li>Shows existing notes as markers on the map</li>
            <li>Requires the event's circuit to have a track map uploaded</li>
          </ul>
          <h3>Adding Notes</h3>
          <ul>
            <li>Click anywhere on the track image to place a note</li>
            <li>A dialog opens for text description</li>
            <li>Add balance sliders for each track phase</li>
            <li>Notes are positioned as percentages of image coordinates</li>
            <li>Multiple notes can be added to the same session</li>
          </ul>
          <h3>Balance Analysis</h3>
          <p>Use the balance sliders to rate handling in five track phases:</p>
          <ul>
            <li><strong>TB (Turn-in/Braking):</strong> Balance during corner entry and braking</li>
            <li><strong>EN (Entry):</strong> Balance through the first part of the corner</li>
            <li><strong>MD (Mid):</strong> Balance in the middle of the corner</li>
            <li><strong>TP (Apex/Turn):</strong> Balance at the corner apex and turn point</li>
            <li><strong>EX (Exit):</strong> Balance during corner exit and acceleration</li>
          </ul>
          <p>Scale: -10 (severe understeer) to +10 (severe oversteer), 0 = neutral</p>
          <h3>Managing Notes</h3>
          <ul>
            <li>Click existing note markers to edit text or balance</li>
            <li>Drag notes to reposition them on the track</li>
            <li>Long press (mobile) or right-click to delete notes</li>
            <li>All changes save automatically to the session</li>
          </ul>
          <h3>Analysis Tips</h3>
          <ul>
            <li>Use balance data to identify where the car feels loose or tight</li>
            <li>Compare balance across different corners on the same track</li>
            <li>Track how setup changes affect balance in specific areas</li>
            <li>Notes help communicate issues to crew or engineers</li>
          </ul>
        </ExpandableSection>

        <ExpandableSection id="circuits" title="Circuit Manager">
          <p>Manage racing circuits and tracks used across multiple events.</p>
          <h3>Adding Circuits</h3>
          <ul>
            <li>Access Circuit Manager from Settings</li>
            <li>Add circuits manually or import from the Circuit Database</li>
            <li>Include country, track length, and corner information</li>
            <li>Upload track map images for visual reference</li>
          </ul>
          <h3>Circuit Database</h3>
          <ul>
            <li>Browse predefined circuits from around the world</li>
            <li>Import circuits with complete track data</li>
            <li>Filter by country or track name</li>
            <li>Save time by using existing circuit information</li>
          </ul>
          <h3>Circuit Editor</h3>
          <ul>
            <li>Edit circuit details after creation</li>
            <li>Update track maps and layout images</li>
            <li>Add or modify corner information</li>
            <li>Circuits are shared across all your events</li>
          </ul>
          <h3>Track Maps</h3>
          <ul>
            <li>Upload high-quality track layout images</li>
            <li>Images are used in Track Map analysis</li>
            <li>Support for various image formats</li>
            <li>Maps are cached locally for offline access</li>
          </ul>
        </ExpandableSection>

        <ExpandableSection id="tire-management" title="Tire Management">
          <p>Track tire usage, wear, and performance across sessions.</p>
          <h3>Tire Parameters</h3>
          <ul>
            <li>Set tire pressures in vehicle setup</li>
            <li>Track tire compounds and expected life</li>
            <li>Monitor pressure changes between sessions</li>
            <li>Record accumulated mileage on tire sets</li>
          </ul>
          <h3>Session Tire Data</h3>
          <ul>
            <li>Record starting and ending pressures</li>
            <li>Note tire wear patterns and degradation</li>
            <li>Track how tires perform in different conditions</li>
            <li>Correlate tire data with lap time improvements</li>
          </ul>
          <h3>Tire Analysis</h3>
          <ul>
            <li>Identify optimal pressure ranges</li>
            <li>Track tire life and replacement timing</li>
            <li>Compare performance between tire compounds</li>
            <li>Use data to optimize tire management strategies</li>
          </ul>
        </ExpandableSection>

        <ExpandableSection id="backup-sync" title="Backup & Sync">
          <p>Your data is automatically protected and synchronized across devices.</p>
          <h3>Automatic Backup</h3>
          <ul>
            <li>Data backs up every 5 minutes when online</li>
            <li>All vehicles, events, sessions, and circuits included</li>
            <li>Backups stored securely in Firebase</li>
            <li>Old backups automatically cleaned up</li>
          </ul>
          <h3>Manual Backup</h3>
          <ul>
            <li>Create backups on demand from Settings</li>
            <li>Download backups as JSON files to your device</li>
            <li>Useful for local storage or sharing</li>
            <li>Backups include all your data and images</li>
          </ul>
          <h3>Restoring Data</h3>
          <ul>
            <li>Restore from automatic backups in Settings</li>
            <li>Upload JSON backup files to restore</li>
            <li>Creates a new backup before restoring</li>
            <li>Restores all data including images and settings</li>
          </ul>
          <h3>Offline Functionality</h3>
          <ul>
            <li>App works fully offline with cached data</li>
            <li>Changes sync automatically when connection returns</li>
            <li>Images cached locally for fast loading</li>
            <li>Sync status shown in header (cloud icon)</li>
          </ul>
          <h3>Cross-Device Sync</h3>
          <ul>
            <li>Data syncs across all your devices</li>
            <li>Changes appear in real-time on other devices</li>
            <li>Requires same Firebase account</li>
            <li>Works with web, mobile, and desktop versions</li>
          </ul>
        </ExpandableSection>

        <ExpandableSection id="performance-graphs" title="Power & Performance Graphs">
          <p>Analyze engine performance and optimize vehicle dynamics.</p>
          <h3>Power Curves</h3>
          <ul>
            <li>Enter RPM vs Power/Torque data in Drivetrain tab</li>
            <li>View power delivery across the RPM range</li>
            <li>Compare different engine configurations</li>
            <li>Identify peak power and torque points</li>
          </ul>
          <h3>Tractive Effort Graphs</h3>
          <ul>
            <li>Visualize acceleration potential at different speeds</li>
            <li>Based on power curve and vehicle weight</li>
            <li>Help optimize gear ratios and final drive</li>
            <li>Compare different powertrain setups</li>
          </ul>
          <h3>Gearing Calculator</h3>
          <ul>
            <li>Calculate optimal gear ratios for specific tracks</li>
            <li>Input track data and vehicle parameters</li>
            <li>Find ideal RPM ranges for each gear</li>
            <li>Maximize acceleration and top speed</li>
          </ul>
          <h3>Analysis Applications</h3>
          <ul>
            <li>Understand how setup changes affect performance</li>
            <li>Optimize gear ratios for different circuits</li>
            <li>Compare engine modifications</li>
            <li>Identify performance bottlenecks</li>
          </ul>
        </ExpandableSection>

        <ExpandableSection id="mobile-desktop" title="Mobile & Desktop Usage">
          <p>The app is fully responsive and optimized for all devices.</p>
          <h3>Mobile Features</h3>
          <ul>
            <li>Use scroll inputs for precise parameter adjustments</li>
            <li>Touch and drag to reorder parameters and groups</li>
            <li>Long press to access context menus</li>
            <li>Swipe gestures for navigation</li>
            <li>Optimized layouts for small screens</li>
          </ul>
          <h3>Desktop Features</h3>
          <ul>
            <li>Mouse and keyboard shortcuts</li>
            <li>Larger parameter adjustment controls</li>
            <li>Multi-column layouts for better space utilization</li>
            <li>Drag and drop with mouse</li>
            <li>Full keyboard navigation support</li>
          </ul>
          <h3>Cross-Platform Consistency</h3>
          <ul>
            <li>Same features and data across all platforms</li>
            <li>Automatic sync between devices</li>
            <li>Responsive design adapts to screen size</li>
            <li>Touch and mouse input both supported</li>
          </ul>
        </ExpandableSection>

        <ExpandableSection id="advanced-features" title="Advanced Features">
          <h3>Parameter Management</h3>
          <ul>
            <li>Mandatory parameters cannot be deleted (defined per system)</li>
            <li>Custom parameters can be added with custom units and ranges</li>
            <li>Parameters can be reordered within groups</li>
            <li>Groups can be reordered and renamed</li>
            <li>Duplicate parameters across different tabs</li>
          </ul>
          <h3>Data Relationships</h3>
          <ul>
            <li>Vehicles contain master parameter templates</li>
            <li>Events link vehicles to circuits and dates</li>
            <li>Sessions inherit from events but can override parameters</li>
            <li>Track maps are circuit-based but notes are session-specific</li>
            <li>Circuits are shared across multiple events</li>
          </ul>
          <h3>Duplication Features</h3>
          <ul>
            <li>Duplicate entire vehicles to create variations</li>
            <li>Duplicate session setups to copy parameter changes</li>
            <li>Duplicate events with all their sessions</li>
            <li>Duplication preserves all custom groups and parameters</li>
          </ul>
          <h3>Advanced Sync Features</h3>
          <ul>
            <li>Real-time collaboration (same account on multiple devices)</li>
            <li>Conflict resolution for simultaneous edits</li>
            <li>Selective sync options (coming soon)</li>
            <li>Data export/import capabilities</li>
          </ul>
          <h3>Performance Optimization</h3>
          <ul>
            <li>Automatic image compression and caching</li>
            <li>Lazy loading of large datasets</li>
            <li>Efficient data structures for fast searches</li>
            <li>Background sync to minimize UI blocking</li>
          </ul>
        </ExpandableSection>
      </div>
    </div>
  );
}

export default Help;